import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PolicyVersion {
  document_type: string;
  version: string;
  effective_date: string;
}

interface PolicyCheckResult {
  needsAcceptance: boolean;
  policyType: "privacy" | "terms" | null;
  newVersion: string;
  effectiveDate: string;
}

export const usePolicyCheck = () => {
  const [policyCheck, setPolicyCheck] = useState<PolicyCheckResult>({
    needsAcceptance: false,
    policyType: null,
    newVersion: "",
    effectiveDate: "",
  });

  useEffect(() => {
    checkPolicyUpdates();
  }, []);

  const checkPolicyUpdates = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get latest policy versions
      const { data: latestPolicies } = await supabase
        .from("legal_document_versions")
        .select("document_type, version, effective_date")
        .order("effective_date", { ascending: false })
        .limit(2);

      if (!latestPolicies || latestPolicies.length === 0) return;

      // Get user's consent history
      const { data: userConsents } = await supabase
        .from("user_consents")
        .select("consent_type, consent_version, consent_timestamp")
        .eq("user_id", user.id)
        .in("consent_type", ["privacy", "terms"])
        .order("consent_timestamp", { ascending: false });

      // Check if user needs to accept updated policies
      for (const policy of latestPolicies) {
        const userConsent = userConsents?.find(
          (c) => c.consent_type === policy.document_type
        );

        // If no consent or version mismatch, user needs to accept
        if (!userConsent || userConsent.consent_version !== policy.version) {
          setPolicyCheck({
            needsAcceptance: true,
            policyType: policy.document_type as "privacy" | "terms",
            newVersion: policy.version,
            effectiveDate: policy.effective_date,
          });
          break;
        }
      }
    } catch (error) {
      console.error("Error checking policy updates:", error);
    }
  };

  const markPolicyAccepted = () => {
    setPolicyCheck({
      needsAcceptance: false,
      policyType: null,
      newVersion: "",
      effectiveDate: "",
    });
  };

  return {
    ...policyCheck,
    markPolicyAccepted,
    recheckPolicies: checkPolicyUpdates,
  };
};
